import { useQuery } from "@tanstack/react-query";
import { UtensilsCrossed } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import type { MenuDraft, MenuLanguage } from "@qassist/schemas";
import { api } from "./api.js";
import { MenuRenderer } from "./MenuRenderer.js";

interface PublicMenuResponse {
  slug: string;
  version: number;
  publishedAt: string;
  menu: MenuDraft;
  assetUrls: Record<string, string>;
}

function publicRoute(pathname: string, slug: string) {
  const relative = pathname.slice(`/menu/${slug}`.length).split("/").filter(Boolean);
  if (relative[0] === "category" && relative[1]) {
    return {
      categoryId: relative[1],
      ...(relative[2] === "item" && relative[3] ? { itemId: relative[3] } : {}),
    };
  }
  return {};
}

export function MenuPublicPage() {
  const { restaurantSlug = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ["public-menu", restaurantSlug],
    queryFn: () => api<PublicMenuResponse>(`/api/v1/menu/${encodeURIComponent(restaurantSlug)}`),
    retry: 1,
    staleTime: 60_000,
  });
  const storageKey = `qassist.menu.language:${restaurantSlug}`;
  const [language, setLanguage] = useState<MenuLanguage>(() =>
    localStorage.getItem(storageKey) === "en" ? "en" : "ar",
  );
  useEffect(() => {
    if (!query.data || localStorage.getItem(storageKey)) return;
    setLanguage(query.data.menu.profile.defaultLanguage);
  }, [query.data, storageKey]);
  useEffect(() => {
    localStorage.setItem(storageKey, language);
  }, [language, storageKey]);
  useEffect(() => {
    if (!query.data) return;
    document.title = `${query.data.menu.profile.name[language]} · Menu`;
  }, [language, query.data]);
  const route = useMemo(
    () => publicRoute(location.pathname, restaurantSlug),
    [location.pathname, restaurantSlug],
  );
  if (query.isPending)
    return (
      <main className="menu-public-state" aria-busy="true">
        <UtensilsCrossed />
        <h1>Opening menu</h1>
        <p>جاري تحميل القائمة…</p>
        <span className="menu-public-state__loader" />
      </main>
    );
  if (query.isError || !query.data)
    return (
      <main className="menu-public-state">
        <UtensilsCrossed />
        <h1>Menu unavailable</h1>
        <p>هذه القائمة غير منشورة أو غير متاحة حالياً.</p>
        <button type="button" onClick={() => void query.refetch()}>
          Try again · إعادة المحاولة
        </button>
      </main>
    );
  return (
    <MenuRenderer
      menu={query.data.menu}
      language={language}
      assetUrls={query.data.assetUrls}
      route={route}
      onLanguageChange={setLanguage}
      onHome={() => navigate(`/menu/${restaurantSlug}`)}
      onCategory={(categoryId) => navigate(`/menu/${restaurantSlug}/category/${categoryId}`)}
      onItem={(categoryId, itemId) =>
        navigate(`/menu/${restaurantSlug}/category/${categoryId}/item/${itemId}`)
      }
    />
  );
}
